/**
 * English - `tipsContent`: the ⓘ explanations beside content and
 * communications fields (pages, blog, menus, metafields, metaobjects, email
 * templates, the email designer and policy acceptance). One key per field,
 * `<form>.<field>`, passed as the field's `info`.
 * Conventions: see `common.ts`.
 */
const tipsContent: Record<string, string> = {
  // Blogs
  'blog.name':
    'The name of this blog, shown to customers as its heading. Its web address is made from this name when you create it and does not change afterwards. Up to 120 characters. Example: Recipes',
  'blog.description':
    'A short line about what this blog covers, shown to customers on the blog page. Up to 500 characters. Optional. Example: Easy home recipes using our spices.',

  // Blog posts
  'blog_post.title':
    'The headline of your post, shown at the top of the post, on the blog page and in the browser tab. Up to 200 characters. Example: 5 Ways to Style a Cotton Kurta This Diwali',
  'blog_post.content':
    'The main body of the post. You can add headings, lists, bold text and links using the toolbar. Write it the way you would explain it to a customer in your shop.',
  'blog_post.seo_title':
    'The title Google and other search engines show for this post. Leave it blank to use the post title. Up to 160 characters. Optional.',
  'blog_post.meta_description':
    'The short summary that appears under the title in search results. One or two sentences that make people want to click. Up to 320 characters. Optional.',
  'blog_post.visibility':
    'Hidden keeps the post as a draft that only you can see. Visible publishes it on your blog for customers to read.',
  'blog_post.visible_from':
    'The date and time the post goes live for customers. Pick a future date to schedule it; until then it stays hidden. Example: 25 Oct, 9:00 AM for a Diwali post.',
  'blog_post.blog':
    'Which of your blogs this post belongs to. The post appears in that blog and under its web address.',
  'blog_post.cover_image':
    'The main picture for this post, shown on the blog page and at the top of the post. A wide, bright photo works best. Optional.',
  'blog_post.excerpt':
    'A short teaser shown with the post in the blog list, before customers open it. Up to 500 characters. Optional. Example: Our five favourite ways to wear cotton this festive season.',
  'blog_post.author':
    'The name shown as the writer of the post. Leave it blank to show no author. Up to 120 characters. Optional. Example: Priya Sharma',
  'blog_post.tags':
    'Topic words that group related posts together. Type a tag and press Enter or a comma to add it. Up to 20 tags. Example: festive, cotton, styling',

  // Menus
  'menu.name':
    'A name to recognise this menu by, such as where it appears on your store. Customers do not see it. A handle for your theme is made from it when you first save. Example: Main menu',
  'menu_item.label':
    'The words customers see for this link in the menu. Keep it short so it fits on a phone screen. Example: New Arrivals',
  'menu_item.link':
    'Where the link takes customers. Pick one of your store pages from the suggestions, or type a full web address for another site. Example: /collections/all or https://instagram.com/yourshop',

  // Pages
  'page_new.title':
    'The name of the new page, shown at the top of it and used to build its web address. Choose it carefully, as the address stays the same later. Example: About us',
  'page_new.template':
    'A ready-made starting layout with sample sections you can edit. Choose Blank page to start with nothing.',
  'page.title':
    'The name of this page, used in the browser tab, in menu links and in search results. Up to 200 characters. Example: Shipping policy',
  'page.show_title':
    'When on, the page title is shown as a heading above the sections. Turn it off if your first section already has its own heading, so the name does not appear twice.',
  'page.visibility':
    'Hidden keeps the page as a draft only you can see. Visible publishes it so customers can open it and you can link to it from menus.',
  'page.visible_from':
    'The date and time the page goes live for customers. Pick a future date to schedule it; until then it stays hidden.',
  'page.seo_title':
    'The title search engines show for this page. Leave it blank to use the page title. Up to 160 characters. Optional.',
  'page.meta_description':
    'The short summary shown under the title in search results. One or two clear sentences about the page. Up to 320 characters. Optional.',

  // Metafields
  'metafield.name':
    'The name of this extra product field, shown on the product form. A key for your theme is made from it. Up to 120 characters. Example: Fabric or Care instructions',
  'metafield.type':
    'What kind of value the field holds, so the product form shows the right input. For example, Number for weight in grams, Colour for a shade, or Metaobject to pick from entries you created.',
  'metafield.points_at':
    'Which metaobject type this field picks from. On each product you will choose one of its entries. Example: Colour swatch',
  'metafield.list':
    'When on, a product can hold several values for this field, in order. When off, it holds just one. Example: several fabric types on one product.',
  'metafield.storefront':
    'When on, this field is sent to your store with the product, so your theme can show it to customers. When off, it stays internal and only your team sees it.',

  // Metaobjects
  'metaobject.name':
    'The name of this type of content, such as a size chart or a colour swatch. A handle for your theme is made from it and does not change. Up to 120 characters. Example: Colour swatch',
  'metaobject.description':
    'A note for your team about what this type of content is for. Customers do not see it. Up to 500 characters. Optional.',
  'metaobject.storefront':
    'When on, your store theme can read and show entries of this type to customers. When off, the entries are for internal use only.',
  'metaobject_field.name':
    'The name of this field in every entry of this type. A key is made from it for your theme. Example: Hex code or Fabric',
  'metaobject_field.type':
    'What kind of value this field holds, so each entry shows the right input: text, number, true or false, web address, colour or date.',
  'metaobject_entry.name':
    'The name of this entry, used to pick it in lists and on products. Up to 200 characters. Example: Beige',
  'metaobject_entry.status':
    'Active entries can be shown on your store. Draft entries are kept but stay hidden from customers until you make them Active.',

  // Email templates
  'email.enabled':
    'When on, customers get this email automatically when the event happens, such as an order being placed. When off, nobody gets it, but your wording is kept.',
  'email.subject':
    'The subject line customers see in their inbox. You can add placeholders from the list below, in double curly brackets. Between 3 and 200 characters. Example: Your order from our shop is confirmed',
  'email.body':
    'The text of the email. Add placeholders from the list below, in double curly brackets, to fill in details like the customer name or order number. Required placeholders must stay in. Between 10 and 8,000 characters.',
  'test_email.recipient':
    'The email address to send a test copy to, usually your own. It uses sample details, and nothing is sent to your customers. Example: owner@yourshop.in',

  // Email designer - whole email
  'design.preheader':
    'A short line most inboxes show next to or under the subject, before the email is opened. Use it to add a reason to open. Optional. Example: Your order ships within 2 days.',
  'design.font':
    'The typeface for the whole email. Blocks use it unless you choose a different one for them. System picks the standard font of each phone or computer.',
  'design.text_colour':
    'The main colour for text in this email. Pick a dark shade on a light background so it is easy to read.',
  'design.link_colour':
    'The colour of links inside the text of this email. Choose one that stands out from the text colour, such as your brand colour.',
  'design.page_background':
    'The colour behind the email, filling the rest of the screen around it.',
  'design.content_background':
    'The colour of the email area itself, behind all the blocks. White or a very light shade is easiest to read.',
  'design.width':
    'How wide the email is on a computer, in pixels. On phones it shrinks to fit the screen. 600 pixels suits almost every email app.',
  'design.corners':
    'How rounded the corners of the email area are. 0 gives sharp corners; higher numbers make them rounder.',
  'design.keep_name':
    'A name for this saved block so you can find it and reuse it in your other emails. Up to 80 characters. Example: Diwali offer banner',

  // Email designer - spacing and background of a block
  'box.space_above':
    'Empty space above this block, in pixels, inside its background. Increase it to give the block more breathing room.',
  'box.space_below':
    'Empty space below this block, in pixels, inside its background. Increase it to separate the block from the next one.',
  'box.space_sides':
    'Empty space on the left and right of this block, in pixels. The same amount is used on both sides.',
  'box.background':
    'A colour behind this block only. Leave it unset to show the email background.',
  'box.border':
    'The thickness of a line drawn around this block, in pixels. Set it to 0 for no border.',
  'box.border_colour': 'The colour of the line drawn around this block.',
  'box.corners':
    'How rounded the corners of this block are, in pixels. 0 gives sharp corners.',

  // Email designer - text style
  'type.font':
    'The typeface for this block. Choose Same as the email to keep it matching the rest; pick another only when this block should stand out.',
  'type.size': 'The size of the text in this block, in pixels. 14 to 16 suits body text.',
  'type.colour':
    'The colour of the text in this block. Leave it unset to use the email text colour.',
  'type.align': 'Whether the text lines up on the left, in the centre or on the right.',
  'type.line_height':
    'The space between lines of text. 1 is tight; around 1.5 is easy to read for longer text.',

  // Email designer - blocks
  'text.words':
    'The words of this paragraph. Press Enter for a new line. Placeholders in double curly brackets are filled in when the email is sent.',
  'heading.text':
    'The heading text for this part of the email. Keep it short and clear. Example: Thank you for your order!',
  'heading.level':
    'How important this heading is. Use Main heading once at the top, and Section or Small headings for parts below it.',
  'button.label':
    'The words on the button. Say what happens when it is pressed. Example: Track your order',
  'button.link':
    'The web address the button opens. Use a full https:// address, or a placeholder such as the order link, which is filled in for each customer.',
  'button.background': 'The fill colour of the button. Your brand colour usually works well.',
  'button.text_colour':
    'The colour of the words on the button. Pick one that is easy to read on the button colour.',
  'button.size': 'How big the button is: small, medium or large.',
  'button.corners':
    'How rounded the button corners are, in pixels. 0 gives a square button; higher numbers make it rounder.',
  'button.align': 'Whether the button sits on the left, in the centre or on the right.',
  'button.full_width':
    'When on, the button stretches across the whole width of the email. This is easier to tap on a phone.',
  'image.picture':
    'Upload a picture from your device, up to 8 MB. JPG, PNG, WebP, GIF and AVIF are accepted, and it is resized for email.',
  'image.address':
    'The web address of the picture. It fills in when you upload, or paste the address of a picture already online. It must start with https://.',
  'image.alt':
    'A few words describing the picture, shown when pictures are turned off and read aloud by screen readers. Example: Blue cotton kurta on a model',
  'image.link':
    'The web address that opens when the picture is pressed. Use a full https:// address or a placeholder. Optional.',
  'image.width':
    'How wide the picture is, as a percentage of the email width. 100% fills the whole width.',
  'image.align': 'Whether the picture sits on the left, in the centre or on the right.',
  'image.corners':
    'How rounded the corners of the picture are, in pixels. 0 gives sharp corners.',
  'divider.colour': 'The colour of the dividing line.',
  'divider.thickness': 'How thick the dividing line is, in pixels.',
  'divider.style': 'How the line is drawn: solid, dashed or dotted.',
  'divider.width':
    'How long the line is, as a percentage of the email width. 100% runs edge to edge.',
  'spacer.height':
    'The height of this empty space, in pixels. Use it to add room between two blocks.',
  'list.numbered':
    'When on, the list is numbered 1, 2, 3. When off, each item starts with a bullet point.',
  'list.item': 'The text of one point in the list. You can add up to 20 items.',
  'social.platform':
    'Which social media service this link goes to. It sets the icon or name shown in the email.',
  'social.url':
    'The full address of your page on this service. Example: https://instagram.com/yourshop',
  'social.style':
    'Coloured tiles show a small icon for each service. Text links show the service names as plain links.',
  'social.size': 'The size of each icon or tile, in pixels.',
  'social.align': 'Whether the social links sit on the left, in the centre or on the right.',
  'social.tile_colour':
    'One colour for every tile. Leave it unset to use each service’s own brand colour.',
  'banner.heading':
    'The large text on the banner. Keep it short and bold. Example: Festive Sale - Up to 40% Off',
  'banner.text':
    'A line or two under the banner heading with more detail. Optional. Example: On all kurtas and sarees till Sunday.',
  'banner.button_label':
    'The words on the banner button. Leave it empty for a banner without a button. Example: Shop now',
  'banner.button_link':
    'Where the banner button takes customers. Use a full https:// address or a placeholder such as your store link.',
  'banner.background':
    'The banner colour. It also shows when the background picture does not load, so choose one the text reads well on.',
  'banner.background_image':
    'A picture behind the banner text. Some email apps do not show background pictures, so the background colour is used there instead. Optional.',
  'banner.text_colour': 'The colour of the heading and text on the banner.',
  'banner.button_background': 'The fill colour of the banner button.',
  'banner.button_colour':
    'The colour of the words on the banner button. Pick one that is easy to read on the button colour.',
  'banner.height': 'The height of the banner, in pixels.',
  'banner.align': 'Whether the banner text sits on the left, in the centre or on the right.',
  'header.logo':
    'Your shop logo, shown at the top of the email. Leave it empty to show your shop name as text instead.',
  'header.title':
    'The text shown at the top when there is no logo. Leave it empty to use your shop name.',
  'header.tagline':
    'A short line shown under your logo or name. Optional. Example: Handloom sarees from Varanasi',
  'header.logo_width': 'How wide your logo is shown, in pixels.',
  'header.align': 'Whether the logo and name sit on the left, in the centre or on the right.',
  'footer.text':
    'The small print at the bottom of the email, such as your business address or a line about who sent it. Example: Sharma Textiles, MG Road, Jaipur 302001',
  'footer.link_label':
    'The words shown for this footer link. Example: Contact us',
  'footer.link_url':
    'The web address this footer link opens. Example: https://yourshop.in/pages/contact',
  'footer.link_colour': 'The colour of the links in the footer.',
  'columns.gap': 'The space between the columns, in pixels.',
  'columns.stack':
    'When on, the columns sit one below the other on a phone, which is easier to read. When off, they stay side by side even on small screens.',

  // Policies
  'policy.confirm':
    'Tick this to confirm you have read this policy and accept it for your business, then press Accept. Your acceptance is recorded against this version of the policy.',

  // >>> QA phase 5 additions
  'metafield.product_value':
    'The value of “{name}” for this product. The field itself is set up under Content → Product fields.',
  'metaobject_entry.field':
    'What this entry says for “{name}”. Every product that points at the entry shows it.',
  'translation.field':
    '“{field}” as shoppers reading {language} see it. Leave it blank to show the original instead.',
  'translation.field_default':
    '“{field}” in your store’s default language - the original every translation is made from.',
};

export default tipsContent;
